import React, {useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {
  Calculator,
  BookOpen,
  TrendingUp,
  Store,
  Briefcase,
  Megaphone,
  Rocket,
  Receipt,
  Landmark,
  ShieldCheck,
  Percent,
  Users,
  Layers,
  ArrowRight,
} from "lucide-react";
import {COMMERCIAL_SUBJECTS} from "./landingData";

const ICONS = {
  Calculator,
  BookOpen,
  TrendingUp,
  Store,
  Briefcase,
  Megaphone,
  Rocket,
  Receipt,
  Landmark,
  ShieldCheck,
  Percent,
  Users,
};

// Layered gradients + highlights + soft shadow give the icons a lightweight
// "3D" feel without external artwork.
function SubjectIcon({accent, Icon}) {
  const [from, to] = accent;
  return (
    <div
      className="relative h-14 w-14 shrink-0 rounded-2xl"
      style={{
        background: `linear-gradient(145deg, ${from}, ${to})`,
        boxShadow: `0 12px 22px -10px ${to}cc, inset 0 2px 3px rgba(255,255,255,0.55)`,
      }}
    >
      <div
        className="absolute inset-0 rounded-2xl"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.45), rgba(255,255,255,0) 55%)",
        }}
      />
      <div className="relative flex h-full w-full items-center justify-center">
        <Icon className="h-6 w-6 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]" strokeWidth={2.2} />
      </div>
    </div>
  );
}

function SubjectCard({subject, hoveredId, setHoveredId}) {
  const navigate = useNavigate();
  const ref = useRef(null);
  const detailsRef = useRef(null);
  const [pos, setPos] = useState({top: 0, left: 0});
  const isOpen = hoveredId === subject.id;
  const Icon = ICONS[subject.icon] || BookOpen;
  const [from, to] = subject.accent;

  // Anchor the popover to the right edge of the hovered card. If it wouldn't
  // fit on the right, flip it to the left of the card (restored original
  // SubjectsDisplay behaviour).
  const show = () => {
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();
    const width = 340;
    const gap = 20;

    let left =
      window.innerWidth - rect.right < width + 40
        ? rect.left - width - gap
        : rect.right + gap;

    left = Math.max(10, Math.min(window.innerWidth - width - 10, left));

    setPos({top: Math.max(10, rect.top + window.scrollY - 40), left});
    setHoveredId(subject.id);
  };

  const hide = () => setHoveredId(null);

  return (
    <>
      <a
        ref={ref}
        href="https://study.filloptech.com/login"
        target="_blank"
        rel="noopener noreferrer"
        onMouseEnter={show}
        onMouseLeave={hide}
        className="group flex w-[236px] shrink-0 cursor-pointer flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm no-underline transition-all duration-300 hover:-translate-y-1 hover:border-blue-200 hover:shadow-[0_18px_38px_-20px_rgba(15,23,42,0.35)]"
      >
        <div className="flex items-start">
          <SubjectIcon accent={subject.accent} Icon={Icon} />
        </div>

        <div className="min-w-0">
          <h3 className="truncate text-[15px] font-bold text-slate-900 group-hover:text-blue-800">
            {subject.name}
          </h3>
          <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-500">
            {subject.blurb}
          </p>
        </div>
      </a>

      {isOpen && (
        <div
          ref={detailsRef}
          style={{top: pos.top, left: pos.left}}
          className="absolute z-[9999] w-[340px]"
          onMouseEnter={show}
          onMouseLeave={hide}
        >
          <div className="overflow-hidden rounded-2xl border-2 border-blue-100 bg-white shadow-2xl animate-fadeIn">
            <div className="h-2" style={{background: `linear-gradient(90deg, ${from}, ${to})`}} />

            <div className="p-4">
              <div className="mb-2 flex items-start gap-3">
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl shadow-md"
                  style={{background: `linear-gradient(145deg, ${from}, ${to})`}}
                >
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate whitespace-nowrap text-lg font-bold text-slate-900">{subject.name}</h3>
                  <p className="truncate whitespace-nowrap text-xs text-gray-800">
                    {subject.questions} Practice Questions
                  </p>
                </div>
              </div>

              <p className="mb-2 truncate whitespace-nowrap text-xs text-gray-800">{subject.description}</p>

              <div className="mb-3">
                <h4 className="mb-1.5 whitespace-nowrap text-[11px] font-bold uppercase tracking-wider text-gray-800">
                  Key Topics:
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {subject.topics.map((topic, idx) => (
                    <span
                      key={idx}
                      className="flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-gray-100 py-1 text-[11px] font-medium text-gray-800"
                    >
                      <div
                        className="h-1.5 w-1.5 rounded-full"
                        style={{background: `linear-gradient(90deg, ${from}, ${to})`}}
                      />
                      {topic}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mb-3 flex justify-between rounded-xl bg-gray-100 px-3 py-2 text-xs">
                <div>
                  <div
                    className="truncate whitespace-nowrap text-base font-bold"
                    style={{
                      background: `linear-gradient(90deg, ${from}, ${to})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {subject.questions}
                  </div>
                  <div className="truncate whitespace-nowrap text-xs text-gray-800">Questions</div>
                </div>
                <div className="text-right">
                  <div
                    className="truncate whitespace-nowrap text-base font-bold"
                    style={{
                      background: `linear-gradient(90deg, ${from}, ${to})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {subject.difficulty}
                  </div>
                  <div className="truncate whitespace-nowrap text-xs text-gray-800">Difficulty</div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => navigate("/login")}
                className="flex w-full items-center justify-center gap-2 rounded-xl py-2 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:scale-[1.02] hover:opacity-90 hover:shadow-xl"
                style={{background: `linear-gradient(90deg, ${from}, ${to})`}}
              >
                <span className="whitespace-nowrap">Start Learning</span>
                <span>→</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Horizontal row that auto-scrolls toward the pointer when it nears either edge
// (restored from the original SubjectsDisplay behaviour).
function ScrollRow({subjects, hoveredId, setHoveredId}) {
  const ref = useRef(null);
  const mouseX = useRef(0);
  const raf = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const move = (event) => {
      mouseX.current = event.clientX;
    };

    const animate = () => {
      const rect = el.getBoundingClientRect();
      const left = rect.left + 120;
      const right = rect.right - 120;

      let speed = 0;

      if (mouseX.current < left) speed = -4 * (1 - (mouseX.current - rect.left) / 120);
      else if (mouseX.current > right) speed = 4 * (1 - (rect.right - mouseX.current) / 120);

      el.scrollLeft += speed;
      raf.current = requestAnimationFrame(animate);
    };

    const start = () => {
      raf.current = requestAnimationFrame(animate);
    };
    const stop = () => cancelAnimationFrame(raf.current);

    el.addEventListener("mouseenter", start);
    el.addEventListener("mouseleave", stop);
    window.addEventListener("mousemove", move);

    return () => {
      el.removeEventListener("mouseenter", start);
      el.removeEventListener("mouseleave", stop);
      window.removeEventListener("mousemove", move);
      cancelAnimationFrame(raf.current);
    };
  }, []);

  return (
    <div
      ref={ref}
      className="overflow-x-auto select-none pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      <div className="flex min-w-max gap-4">
        {subjects.map((subject) => (
          <SubjectCard
            key={subject.id}
            subject={subject}
            hoveredId={hoveredId}
            setHoveredId={setHoveredId}
          />
        ))}
      </div>
    </div>
  );
}

export default function CommercialSubjects() {
  const navigate = useNavigate();
  const [hoveredId, setHoveredId] = useState(null);

  return (
    <section id="commercial-subjects" className="border-t border-slate-100 bg-slate-50 px-4 py-14">
      <div className="mx-auto max-w-7xl">
        <header className="mb-8 flex flex-wrap items-end justify-between gap-4" data-aos="fade-up">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700">
              <Layers className="h-3.5 w-3.5" /> Commercial subjects
            </span>
            <h2 className="mt-4 text-3xl tracking-tight text-slate-900 sm:text-4xl">
              Explore Commercial Subjects
            </h2>
            <p className="mt-3 text-base text-slate-600">
              Build a strong foundation in business and commerce — from accounting
              and economics to marketing, banking and entrepreneurship.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/courses")}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition-all hover:-translate-y-0.5 hover:border-blue-300 hover:text-blue-800"
          >
            View all subjects <ArrowRight className="h-4 w-4" />
          </button>
        </header>
      </div>

      {/* Two auto-scrolling rows of equal size, edge-to-edge like the original */}
      <ScrollRow subjects={COMMERCIAL_SUBJECTS.slice(0, 6)} hoveredId={hoveredId} setHoveredId={setHoveredId} />
      <ScrollRow subjects={COMMERCIAL_SUBJECTS.slice(6)} hoveredId={hoveredId} setHoveredId={setHoveredId} />
    </section>
  );
}